import {
  dfpSourceDocumentResponse,
  DfpStatementKind,
} from '@interfaces/dfp/response/dfpSourceDocumentsResponse';

/**
 * Textos da Análise Econômica que saíram do `dfp.component.ts` (07/10/2026) para o componente ficar abaixo do
 * teto de ~1.000 linhas do `AGENTS.md` antes de ganhar o aviso de documentos em processamento. Só texto: quem
 * mostra o toast ou a confirmação continua sendo o componente.
 */

/** Rótulo do demonstrativo para o texto da confirmação de remoção. Espelha o do painel de documentos de origem. */
const SOURCE_KIND_LABELS: Record<DfpStatementKind, string> = {
  [DfpStatementKind.IncomeStatement]: 'DRE',
  [DfpStatementKind.BalanceSheet]: 'Balanço',
  [DfpStatementKind.RevenueStatement]: 'Faturamento',
};

/**
 * Descrição da confirmação de "remover documento de origem" (card #2932). Diz **o que sai da tela**
 * (demonstrativo + anos da cobertura), porque quem confirma está olhando uma tabela de números, não uma lista de
 * arquivos.
 */
export function describeSourceRemoval(document: dfpSourceDocumentResponse): string {
  const years = [...new Set(document.coverage.map(item => item.year))].sort((a, b) => a - b);
  return years.length
    ? `Os valores de ${SOURCE_KIND_LABELS[document.kind]} de ${years.join(', ')} saem da Análise ` +
        'Econômica. O arquivo continua guardado e pode ser enviado de novo pela Padronização de Documentos.'
    : 'Este documento não alimenta nenhuma coluna hoje. O arquivo continua guardado.';
}

/**
 * Título e detalhe do toast de falha ao carregar a tela. O discriminador é o `key` do erro, nunca o texto: o
 * `ResultObject` usa `key: "Exception"` quando cai no `catch` (falha técnica) e o nome do campo quando é
 * validação de negócio. Casar pela mensagem quebraria com os resources localizados.
 *
 * - **Falha técnica**: o usuário não causou e não resolve relendo. Diz o que falhou, onde olhar, e preserva o
 *   texto técnico rotulado, para ele repassar ao suporte.
 * - **Validação de negócio**: a mensagem já foi escrita para o usuário e vai como está.
 *
 * @param fallback Texto para quando a API não mandou mensagem nenhuma (falha de rede).
 */
export function describeDfpLoadError(
  error: unknown,
  fallback: string
): { title: string; detail: string } {
  const errors = (error as { errors?: { key?: string; message: string }[] })?.errors ?? [];
  const apiMessage = errors[0]?.message;

  if (errors.some(item => item.key === 'Exception')) {
    return {
      title: 'Não foi possível calcular a Análise Econômica desta empresa',
      detail:
        'Houve uma falha ao processar os dados extraídos desta empresa: os números não puderam ser montados. ' +
        'Confira as extrações em Padronização de dados contábeis e, se o problema continuar, acione o suporte ' +
        `informando este detalhe técnico: "${apiMessage ?? 'sem detalhe'}".`,
    };
  }

  return { title: 'Não foi possível carregar a Análise Econômica', detail: apiMessage || fallback };
}
